import { useCallback, useEffect } from 'react';
import { useToast } from '@/components/ui/Toast';
import { tasksText } from '@/i18n/tasks';
import { pushStore, usePushState } from './store';
import { enablePush, type EnablePushResult } from './subscription';

/** localStorage flag: the automatic permission prompt after login is shown once per login per device. */
export const PUSH_PROMPTED_KEY_PREFIX = 'agrogruda_push_prompted_';

const registeredThisLoad = new Set<string>();

function alreadyPrompted(userId: string): boolean {
  try {
    return localStorage.getItem(PUSH_PROMPTED_KEY_PREFIX + userId) === '1';
  } catch {
    return false;
  }
}

function markPrompted(userId: string): void {
  try {
    localStorage.setItem(PUSH_PROMPTED_KEY_PREFIX + userId, '1');
  } catch {
    // storage unavailable: the Njoftimet button remains
  }
}

/** Tests only. */
export function resetPushAutoRegister(): void {
  registeredThisLoad.clear();
}

/**
 * task.md §4.8 "on first login": once per login per page load, register the worker and subscribe. Permission is
 * requested automatically only the first time on this device; afterwards the 'Njoftimet' button (permission
 * still 'default') asks with a user gesture. A granted permission re-subscribes silently on every load.
 */
export function usePushAutoRegister(userId: string | null): void {
  useEffect(() => {
    if (!userId || registeredThisLoad.has(userId)) return;
    registeredThisLoad.add(userId);
    const request = !alreadyPrompted(userId);
    if (request) markPrompted(userId);
    void enablePush({ request }).then((result) => pushStore.update({ subscribed: result === 'subscribed' }));
  }, [userId]);
}

export function pushResultToast(result: EnablePushResult): string | null {
  switch (result) {
    case 'subscribed':
      return tasksText.push.enabled;
    case 'denied':
      return tasksText.push.denied;
    case 'unsupported':
      return tasksText.push.unsupported;
    case 'unavailable':
      return tasksText.push.unavailable;
    case 'failed':
      return tasksText.push.failed;
    default:
      return null;
  }
}

export interface PushControls {
  /** The 'Njoftimet' button is shown while permission is still 'default'. */
  canAsk: boolean;
  subscribed: boolean;
  ask: () => Promise<EnablePushResult>;
}

/** 'Njoftimet' button behaviour: ask with a gesture and toast the outcome. */
export function usePushControls(): PushControls {
  const toast = useToast();
  const state = usePushState();
  const ask = useCallback(async () => {
    const result = await enablePush({ request: true });
    pushStore.update({ subscribed: result === 'subscribed' });
    const message = pushResultToast(result);
    if (message) toast(message);
    return result;
  }, [toast]);
  return { canAsk: state.permission === 'default', subscribed: state.subscribed, ask };
}
